import { classToPlain, Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import { v4 as uuid } from "uuid";
import { validateJSON } from "../web/validation";
import { MeterEntity } from "./meter";
import { Entity, EntityIdentifier, EntityObject, getRequiredEntityId, isEntity } from "./entity";
import { FreightTypeEntity } from "./freight-type";
import { RegionEntity } from "./region";
import { Quantity } from "../types/types";
import { adjustFromTimeZone, format } from "../date/date";
import { Exportable } from "./exportable";

export const MeterFlowReadingValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    meterId: t.string,
    reading: t.number,
    unitOfMeasure: t.string,
    dttm: t.number,
  }),
  t.partial({
    meterName: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    objectId: t.string,
    objectType: t.string,
    objectName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const MeterFlowReadingArrayValidator = t.array(
  MeterFlowReadingValidator
);

export type MeterFlowReading = t.TypeOf<typeof MeterFlowReadingValidator>;
export type MeterFlowReadingArray = t.TypeOf<
  typeof MeterFlowReadingArrayValidator
>;

@Exclude()
export class MeterFlowReadingEntity implements Entity, Exportable {
  @Expose()
  id!: string;
  @Expose()
  regionId!: string;
  @Expose()
  meterId!: string;
  @Expose()
  meterName?: string;
  @Expose()
  reading!: number;
  @Expose()
  unitOfMeasure!: string;
  @Expose()
  freightTypeId?: string;
  @Expose()
  freightTypeName?: string;
  @Expose()
  objectId?: string;
  @Expose()
  objectType?: string;
  @Expose()
  objectName?: string;
  @Expose()
  dttm!: number;
  @Expose()
  createDttm?: number;
  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'METER_FLOW_READING';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for MeterFlowReadingEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): MeterStep {
    return new MeterFlowReadingBuilder(id);
  }

  static create(builder: MeterFlowReadingBuilder): MeterFlowReadingEntity {
    const entity = new MeterFlowReadingEntity();
    entity.id = builder.id;
    entity.regionId = builder.regionId;
    entity.meterId = builder.meterId;
    entity.meterName = builder.meterName;
    entity.reading = builder.reading;
    entity.unitOfMeasure = builder.unitOfMeasure;
    entity.freightTypeId = builder.freightTypeId;
    entity.freightTypeName = builder.freightTypeName;
    entity.objectId = builder.objectId;
    entity.objectType = builder.objectType;
    entity.objectName = builder.objectName;
    entity.dttm = builder.dttm;
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  static from(reading: MeterFlowReading): MeterFlowReadingEntity {
    const entity: MeterFlowReadingEntity = plainToClass(
      MeterFlowReadingEntity,
      reading,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the MeterFlowReadingEntity to a MeterFlowReading type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<MeterFlowReading> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(MeterFlowReadingValidator, object);
  }

  setMeter(value: string | MeterEntity) {
    if (typeof value === 'string') {
      this.meterId = value;
      this.meterName = undefined;
    } else {
      this.meterId = value.id;
      this.meterName = value.name;

      this.objectId = value.objectId;
      this.objectType = value.objectType;
      this.objectName = value.objectName;
    }
  }

  setRegion(value: string | RegionEntity) {
    this.regionId = getRequiredEntityId(value);
  }

  setReading(value: Quantity) {
    this.reading = value.number;
    this.unitOfMeasure = value.units;
  }

  setFreightType(value: string | FreightTypeEntity | undefined) {
    if (value) {
      if (typeof value === 'string') {
        this.freightTypeId = value;
        this.freightTypeName = undefined;
      } else {
        this.freightTypeId = value.id;
        this.freightTypeName = value.name;
      }
    } else {
      this.freightTypeId = undefined;
      this.freightTypeName = undefined;
    }
  }

  setObject(value: EntityObject | undefined) {
    if (value) {
      if (isEntity(value)) {
        this.objectId = value.id;
        this.objectType = value.entityType;
        this.objectName = value.displayName;
      } else {
        this.objectId = value.objectId;
        this.objectType = value.objectType;
        this.objectName = value.objectName;
      }
    } else {
      this.objectId = undefined;
      this.objectType = undefined;
      this.objectName = undefined;
    }
  }

  readonly CSV_HEADERS: string[] = [
    'Date',
    'Meter',
    'Reading',
    'Unit of Measure',
    'Freight Type',
    'Object',
  ];

  asCsv(): string[] {
    return [
      this.dttm
        ? format(adjustFromTimeZone(this.dttm), 'yyyy-MM-dd HH:mm:ss')
        : '',
      this.meterName ?? '',
      this.reading ? this.reading.toString() : '',
      this.unitOfMeasure ?? '',
      this.freightTypeName ?? '',
      this.objectName ?? '',
    ];
  }
}

interface DateStep {
  setDate(value: number): MeterFlowReadingBuilder;
}

interface ReadingStep {
  setReading(value: Quantity): DateStep;
}

interface RegionStep {
  setRegion(value: string | RegionEntity): ReadingStep;
}

interface MeterStep {
  setMeter(value: string | MeterEntity): RegionStep;
}

export class MeterFlowReadingBuilder
  implements MeterStep, RegionStep, ReadingStep, DateStep
{
  id: string;
  regionId!: string;
  meterId!: string;
  meterName?: string;
  reading!: number;
  unitOfMeasure!: string;
  freightTypeId?: string;
  freightTypeName?: string;
  objectId?: string;
  objectType?: string;
  objectName?: string;
  dttm!: number;
  createDttm?: number = undefined;
  lastModifiedDttm?: number = undefined;

  constructor(id: string = uuid()) {
    this.id = id;
  }

  setMeter(value: string | MeterEntity): RegionStep {
    if (typeof value === 'string') {
      this.meterId = value;
      this.meterName = undefined;
    } else {
      this.meterId = value.id;
      this.meterName = value.name;

      this.objectId = value.objectId;
      this.objectType = value.objectType;
      this.objectName = value.objectName;
    }
    return this;
  }

  setRegion(value: string | RegionEntity): ReadingStep {
    this.regionId = getRequiredEntityId(value);
    return this;
  }

  setReading(value: Quantity): DateStep {
    this.reading = value.number;
    this.unitOfMeasure = value.units;
    return this;
  }

  setDate(value: number): MeterFlowReadingBuilder {
    this.dttm = value;
    return this;
  }

  setFreightType(
    value: string | FreightTypeEntity | undefined
  ): MeterFlowReadingBuilder {
    if (value) {
      if (typeof value === 'string') {
        this.freightTypeId = value;
        this.freightTypeName = undefined;
      } else {
        this.freightTypeId = value.id;
        this.freightTypeName = value.name;
      }
    } else {
      this.freightTypeId = undefined;
      this.freightTypeName = undefined;
    }
    return this;
  }

  setObject(value: EntityObject | undefined): MeterFlowReadingBuilder {
    if (value) {
      if (isEntity(value)) {
        this.objectId = value.id;
        this.objectType = value.entityType;
        this.objectName = value.displayName;
      } else {
        this.objectId = value.objectId;
        this.objectType = value.objectType;
        this.objectName = value.objectName;
      }
    } else {
      this.objectId = undefined;
      this.objectType = undefined;
      this.objectName = undefined;
    }
    return this;
  }

  setCreateDttm(value: number | undefined): MeterFlowReadingBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): MeterFlowReadingBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  build(): MeterFlowReadingEntity {
    return MeterFlowReadingEntity.create(this);
  }
}

export interface MeterFlowReadingSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  freightTypeIds?: string[];
  meterIds?: string[];
  entityIds?: EntityIdentifier[];
}

export const MeterFlowReadingImportResultsValidator = t.intersection([
  t.type({
    readings: t.array(MeterFlowReadingValidator),
    missingMappings: t.array(t.string),
  }),
  t.partial({
    // partial props here
  }),
]);

export type MeterFlowReadingImportResults = t.TypeOf<
  typeof MeterFlowReadingImportResultsValidator
>;
